// src/components/ModalRoot.js
import React, { useContext, useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useSwipeable } from 'react-swipeable';
import { RemoveScroll } from 'react-remove-scroll';

import { ModalContext } from '../context/ModalContext';
import { TabContext }   from '../context/TabContext';

import './ModalRoot.css';

export default function ModalRoot() {
  const { modalStacks, closeModal } = useContext(ModalContext);
  const { activeTab }               = useContext(TabContext);

  // ─── Calcular dinámicamente top/bottom/height ────────────────────────
  useEffect(() => {
    const HEADER_H = 60;
    const FOOTER_H = 60;
    const KB_THRESHOLD = 150; // px para detectar teclado

    function updateVars() {
      const innerH = window.innerHeight;
      const vv     = window.visualViewport;
      const viewH  = vv ? vv.height : innerH;
      const kbH    = innerH - viewH;
      const keyboardOpen = kbH > KB_THRESHOLD;

      // Si hay teclado, top=solo safe-area, bottom=solo safe-area
      // si no, sumamos header/footer
      const topVal = keyboardOpen
        ? `env(safe-area-inset-top,0px)`
        : `calc(env(safe-area-inset-top,0px) + ${HEADER_H}px)`;
      const bottomVal = keyboardOpen
        ? `env(safe-area-inset-bottom,0px)`
        : `calc(env(safe-area-inset-bottom,0px) + ${FOOTER_H}px)`;

      // Altura útil: si teclado, toda la visualViewport; si no, descontar header+footer
      const usable = keyboardOpen ? viewH : viewH - (HEADER_H + FOOTER_H);

      document.documentElement.style.setProperty('--modal-top',    topVal);
      document.documentElement.style.setProperty('--modal-bottom', bottomVal);
      document.documentElement.style.setProperty(
        '--modal-height',
        `${usable}px`
      );
    }

    updateVars();

    // Suscribimos a todos los eventos que pueden cambiar el viewport
    window.addEventListener('resize',       updateVars);
    window.addEventListener('orientationchange', updateVars);
    document.addEventListener('focusin',    updateVars);
    document.addEventListener('focusout',   updateVars);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateVars);
      window.visualViewport.addEventListener('scroll', updateVars);
    }

    return () => {
      window.removeEventListener('resize', updateVars);
      window.removeEventListener('orientationchange', updateVars);
      document.removeEventListener('focusin',   updateVars);
      document.removeEventListener('focusout',  updateVars);

      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateVars);
        window.visualViewport.removeEventListener('scroll', updateVars);
      }
    };
  }, []);

  // ─── Swipe para cerrar el modal ───────────────────────────────────────
  const stacksEntries = useMemo(
    () => Object.entries(modalStacks),
    [modalStacks]
  );
  const [translateX, setTranslateX] = useState(0);
  useEffect(() => setTranslateX(0), [modalStacks, activeTab]);

  const handleCloseTopModal = () => closeModal(activeTab);
  const swipeHandlers = useSwipeable({
    onSwiping: ({ dir, deltaX }) => dir === 'Right' && setTranslateX(deltaX),
    onSwiped:  ({ dir, deltaX }) => {
      if (dir === 'Right' && deltaX > 200) handleCloseTopModal();
      else setTranslateX(0);
    },
    delta: 10,
    preventDefaultTouchmoveEvent: true,
    trackTouch: true,
    trackMouse: false,
  });

  // ─── Si no hay modales abiertos, nada que renderizar ────────────────
  const anyOpen = stacksEntries.some(([,stack]) => stack.length > 0);
  if (!anyOpen) return null;

  // ─── Renderizamos TODOS los modales, pero sólo 'is-active' el de la pestaña actual ─
  return ReactDOM.createPortal(
    <>
      {stacksEntries.map(([tab, stack]) =>
        stack.map((modal, idx) => {
          const isActiveTab = tab === activeTab;
          const isTopModal  = isActiveTab && idx === stack.length - 1;
          const zIndex      = 1000 + (isActiveTab ? idx : 0);

          return (
            <div
              key={`${tab}-${modal.id}`}
              className={`modal-overlay ${modal.overlayClassName || ''} ${
                isActiveTab ? 'is-active' : ''
              }`}
              style={{ zIndex }}
              onClick={isTopModal ? handleCloseTopModal : undefined}
            >
              <RemoveScroll enabled={isTopModal}>
                <div
                  className="modal-content"
                  {...(isTopModal && !modal.disableSwipe ? swipeHandlers : {})}
                  onClick={e => e.stopPropagation()}
                  style={{
                    transform: isTopModal
                      ? `translateX(${translateX}px)`
                      : undefined
                  }}
                >
                  {modal.content}
                </div>
              </RemoveScroll>
            </div>
          );
        })
      )}
    </>,
    document.getElementById('modal-root')
  );
}
